import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { RATING_LABELS } from '../../form/formUtils'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { FileDownload } from '@mui/icons-material'
import { download, generateCsv, mkConfig } from 'export-to-csv'
import esModels from '../models/EnvironmentalAndSafetyModels'
import qfsModels from '../models/QualityAndFSModels'
import { convert } from 'html-to-text'
import { fontWeight } from '@mui/system'

export default function PrintableAssessmentItemList(props={assessment: undefined, includedModels: undefined, exportable: undefined, assessmentGroup: undefined}) {
  const [assessmentItems, setAssessmentItems] = useState([])

  const models = [...qfsModels, ...esModels]

  const getActiveCategories = () => {
    if (props.assessment.filter) {
      const assessmentCategories = JSON.parse(props.assessment.filter)
      return models.filter((m) => {
        return assessmentCategories.includes(m.props.id)
      })
    } else {
      return models
    }
  }

  const extractItemModel = useCallback((model) => {
    let children = []
    
    if (['Category', 'Section', 'Item'].includes(model.type)) {
      children = model.children.flatMap((child) => {
        return extractItemModel(child)
      })
    }
    const itemModel = {
      id: model.props.id,
      type: model.type,
      title: model.props.title,
      maxPoints: model.props.maxPoints ?? '0',
    }
    return [itemModel, ...children]
  }, [])

  const initialize = useCallback(() => {
    const included = props.includedModels ?? []
    const _itemModels = included.flatMap((m) => extractItemModel(m))
    setAssessmentItems(_itemModels)
  }, [extractItemModel, props.includedModels])

  useEffect(() => {
    initialize()
  }, [initialize])

  const formatByType = useCallback((type, value) => {
    console.log(type)
    switch (type.toLowerCase()) {
      case 'item':
        return <Typography variant='body2' style={{fontWeight:'bold'}}>{value}</Typography>
      case 'question':
        return <Typography variant='body2'>{value}</Typography>
      case 'category':
        return <Typography variant='body2' style={{fontWeight:'bold', color:'darkred'}}>{value}</Typography>
      case 'section':
        return <Typography variant='body2' style={{fontWeight:'bold', color:'indianred'}}>{value}</Typography>
      default:
        return <Typography variant='body2'>{value}</Typography>
    }
  }, [])

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'Item',
      grow: false,
      size: 75,
      Cell: ({cell, row}) => {
        return formatByType(row.original.type, cell.getValue())
      }
    },
    {
      accessorKey: 'type',
      header: 'Type',
      grow: false,
      size: 100,
      Cell: ({cell, row}) => {
        return formatByType(row.original.type, cell.getValue())
      }
    },
    {
      accessorKey: 'title',
      header: 'Title',
      grow: true,
      Cell: ({row}) => {
        const title = row.original.title
        if (typeof title === 'string') {
          return formatByType(row.original.type, title)
        } else if (Array.isArray(title)) {
          return (<Stack>{title.map((i, x) => <Typography variant='body2' key={x}>{i}</Typography>)}</Stack>)
        } else if (title?.list !== undefined) {
          return (<Stack>{title.list.map((i, x) => <Typography variant='body2' key={x}>{i}</Typography>)}</Stack>)
        } else if (title?.html !== undefined) {
          return (<div dangerouslySetInnerHTML={{ __html: title.html }}></div>)
        }
      }
    },
    {
      accessorKey: 'maxPoints',
      header: 'Max Points',
      enableColumnFilter: false,
      grow: false,
      size: 100,
      Cell: ({cell, row}) => { return `${row.original.maxPoints === '0' ? '' : row.original.maxPoints}` }
    }
  ], [formatByType])

const table = useMaterialReactTable({
  data: assessmentItems,
  columns: columns,
  layoutMode: 'grid',
  enableDensityToggle: false,
  enableHiding: false,
  enableColumnFilters: true,
  enablePagination: false,
  enableColumnDragging: false,
  enableSorting: false,
  enableColumnActions: false,
  displayColumnDefOptions: {'mrt-row-expand': { size: 30, grow: false } },
  initialState: {
    expanded: true,
    showColumnFilters: props.exportable
  },
  // renderDetailPanel: ( {row} ) => {
  //   const set = Object.entries(row.original)
  //  return (
  //   <Stack spacing={1} paddingLeft={2}>
  //         {(row.original.areaFinding || '').length > 0 && <>
  //         <Typography variant='body2' style={{ fontWeight: 'bold'}}>Area Finding: Observed/Location</Typography>
  //         <Typography paddingLeft={2} style={{ whiteSpace: 'pre-wrap' }}>{row.original.areaFinding}</Typography>
  //         </>}
  //         <Typography variant='body2' style={{ fontWeight: 'bold'}}>Finding: Gaps/Observations</Typography>
  //         <Typography paddingLeft={2} style={{ whiteSpace: 'pre-wrap' }}>{row.original.findingObservations}</Typography>
  //         <Typography variant='body2' style={{ fontWeight: 'bold'}}>Objective Evidence</Typography>
  //         <Typography paddingLeft={2} style={{ whiteSpace: 'pre-wrap' }}>{row.original.objectiveEvidence}</Typography>
  //         <Typography variant='body2' style={{ fontWeight: 'bold'}}>QSE Objective Evidence</Typography>
  //         <Typography paddingLeft={2} style={{ whiteSpace: 'pre-wrap' }}>{row.original.objectiveEvidenceQse}</Typography>
  //       </Stack>
  // )},
  muiTableBodyCellProps: {
    sx: {
      fontSize: '.82rem',
      fontWeight: 'bold',
      padding: '4px !important'
    }
  },
  muiTableHeadCellProps: {
    sx: {
      fontSize: '.9rem !important',
      verticalAlign: 'center'
    }
  },
  renderTopToolbar: true,
  renderBottomToolbarCustomActions: ({table}) => {
    if (props.exportable) return (
    <Stack direction={'row'}>
      <Button onClick={handleExportData} startIcon={<FileDownload />}>Export to File</Button>
    </Stack>
  )}
})

const handleExportData = () => {
  const rowData = table.getPrePaginationRowModel().rows.map((row) => {
    const r = row.original

    let title
    if (typeof r.title === 'string') {
      title = r.title
    } else if (Array.isArray(r.title)) {
      title = r.title.join('\\n')
    } else if (r.title?.list !== undefined) {
      title = r.title.list.join('\\n')
    } else if (r.title?.html !== undefined) {
      title = convert(r.title.html)
    }

    const n = {
      ItemId: r.id,
      Title: title,
      MaxPoints: r.maxPoints === '0' ? '' : r.maxPoints
    }
    return n
  })
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: `${props.assessment.id}-criteria`,
    fileExtension: 'csv',
    quoteStrings: true
  })
  const csv = generateCsv(csvConfig)(rowData);
  download(csvConfig)(csv);
}

return (<>
  {((assessmentItems?.length ?? 0) > 0) && 
    <MaterialReactTable table={table} />
  }
</>)}